import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Tpmsoperations.css";

/* ============================================================
   IMAGES – apni image ke paths yahan badlo (public/TPMS_img/ me rakho)
   ============================================================ */
const IMAGES = {
  dashboard: "/ImagesNew/dashboardcapabilities.jpeg",
};

/* Button links – apne routes ke hisaab se badlo */
const LINKS = { demo: "/contact", team: "/contact" };

/* ============================================================
   CONTENT  (client ka content – yahin se edit karo)
   ============================================================ */
const CONDITIONS = {
  badge: "BUILT FOR THE FIELD",
  title: "Designed for Real-World Toll Plaza Conditions",
  items: [
    { icon: "traffic", title: "Stop-and-Go Traffic Monitoring", text: "Tracks vehicles through changing speeds, temporary stops, and barrier-controlled movement." },
    { icon: "cloud", title: "Challenging Visibility Conditions", text: "Uses video analytics and recovery-oriented workflows to improve monitoring resilience under variable lighting, weather, and partial obstruction, subject to camera image quality." },
    { icon: "refresh", title: "Recovery-Oriented Video Processing", text: "Prioritizes analysis of available recorded footage even when real-time processing experiences delays." },
    { icon: "clock", title: "Recording-Based Timestamps", text: "Uses recording timestamps, including supported video filename timestamps, so vehicle records reflect when the footage was captured rather than when it was processed." },
    { icon: "lanes", title: "Multi-Lane Operational Support", text: "Supports different camera configurations and lane layouts for toll plaza deployments of varying sizes." },
  ],
};

const DASHBOARD = {
  badge: "CENTRALIZED CONTROL",
  title: "Centralized Toll Plaza Monitoring Dashboard",
  text: "The TPMS dashboard helps toll operators manage and review vehicle activity from a centralized interface.",
  listTitle: "Dashboard Capabilities",
  items: [
    "Lane-wise vehicle monitoring",
    "Vehicle passage records",
    "Vehicle classification details",
    "Vehicle images and available video evidence",
    "Historical transaction review",
    "Camera operational status",
    "Processing health and backlog monitoring",
    "Search, filtering, and reporting",
    "Transaction data export",
  ],
  note: "With supported transaction-data integration, these records can also assist revenue assurance and exemption-audit workflows.",
};

const DEPLOY = {
  badge: "DEPLOYMENT",
  title: "Flexible Deployment and Integration",
  text: "TPMS can be adapted to different toll plaza infrastructures and operational requirements.",
  items: [
    { icon: "camera", title: "Existing Camera Integration", text: "Works with compatible installed camera feeds without requiring additional roadside detection hardware." },
    { icon: "server", title: "On-Premises and Cloud Processing", text: "Supports deployment architectures using available or appropriately provisioned local and cloud computing resources." },
    { icon: "lanes", title: "Multi-Lane Scalability", text: "Designed for monitoring multiple toll lanes with centralized processing and reporting capabilities." },
    { icon: "film", title: "Recorded Video Analysis", text: "Supports historical footage analysis, processing recovery, and backlog management." },
    { icon: "api", title: "API-Based Integration", text: "Provides structured data access for supported external systems, dashboards, and reporting platforms." },
  ],
};

const WHY = {
  badge: "WHY TPMS",
  title: "Why Choose TPMS?",
  subtitle: "More Than CCTV. More Than Vehicle Counting.",
  paragraphs: [
    "Traditional CCTV systems record vehicle activity, but they often depend on manual review to extract useful operational information.",
    "TPMS transforms existing camera video into structured vehicle intelligence.",
    "It helps operators understand which vehicles passed through the plaza, when they were recorded, what visual evidence is available, and whether further transaction verification may be required.",
  ],
  listTitle: "TPMS combines:",
  items: [
    "AI-powered vehicle monitoring",
    "Existing camera infrastructure compatibility",
    "Automated visual evidence collection",
    "Fraud and revenue leakage investigation support",
    "Exempted vehicle auditing",
    "Vehicle classification and analytics",
    "Multi-lane operational monitoring",
    "Recorded-video recovery capabilities",
  ],
  closing: "All within a unified intelligent toll monitoring platform.",
};

const CTA = {
  title: "Transform Your Toll Plaza with TPMS",
  subtitle: "Protect Revenue. Improve Accountability. Monitor Every Lane.",
  paragraphs: [
    "Modern toll operations need more than cameras. They need actionable information, reliable evidence, and the ability to investigate discrepancies.",
    "TPMS helps transform existing toll plaza video infrastructure into a vehicle intelligence platform that supports accurate monitoring, stronger financial oversight, and more efficient operations.",
  ],
  strap: "Make Every Vehicle Visible. Make Every Transaction Accountable.",
  primary: "Request a Demo",
  secondary: "Contact Our Team",
  tagline: "TPMS: AI-Powered Toll Plaza Monitoring and Revenue Assurance.",
};

/* ============================================================
   ICONS
   ============================================================ */
const ICONS = {
  traffic: (<><rect x="7" y="2" width="10" height="20" rx="3" /><circle cx="12" cy="7" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="17" r="1.5" /></>),
  cloud: (<><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" /></>),
  refresh: (<><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></>),
  clock: (<><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>),
  lanes: (<><path d="M4 21 8 3M20 21 16 3M12 4v3M12 11v3M12 18v3" /></>),
  camera: (<><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3" /></>),
  server: (<><rect x="2" y="3" width="20" height="8" rx="2" /><rect x="2" y="13" width="20" height="8" rx="2" /><path d="M6 7h.01M6 17h.01" /></>),
  film: (<><rect x="2" y="3" width="20" height="18" rx="2" /><path d="M7 3v18M17 3v18M2 8h5M17 8h5M2 12h20M2 16h5M17 16h5" /></>),
  api: (<><path d="m8 6-6 6 6 6M16 6l6 6-6 6M14 4l-4 16" /></>),
  check: <path d="m20 6-11 11-5-5" />,
  dash: (<><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></>),
};

const Icon = ({ name, size = 26 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);


/* scroll-in animation hook */
const useInView = (threshold = 0.18) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
};

/* stagger helper: style={d(i)} */
const d = (i) => ({ "--i": i });

const Head = ({ badge, title, subtitle, light }) => (
  <header className={`tz__head ${light ? "tz__head--light" : ""}`}>
    <span className="tz__pill tz__r">{badge}</span>
    <h2 className="tz__title tz__r" style={d(1)}>{title}</h2>
    {subtitle && <p className="tz__subtitle tz__r" style={d(2)}>{subtitle}</p>}
    <span className="tz__underline" />
  </header>
);

/* ============================================================
   1. REAL-WORLD CONDITIONS
   ============================================================ */
export const TPMSConditions = () => {
  const [ref, seen] = useInView();
  return (
  <section ref={ref} className={`tz tz--sky ${seen ? "tz--in" : ""}`} id="conditions">
    <div className="tz__container">
      <Head badge={CONDITIONS.badge} title={CONDITIONS.title} />
      <div className="tz__cond-grid">
        {CONDITIONS.items.map((c, i) => (
          <article className="tz__cond tz__r" style={d(i)} key={c.title}>
            <span className="tz__cond-num">{String(i + 1).padStart(2, "0")}</span>
            <span className="tz__icon"><Icon name={c.icon} /></span>
            <h3>{c.title}</h3>
            <p>{c.text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   2. DASHBOARD
   ============================================================ */
export const TPMSDashboard = () => {
  const [ref, seen] = useInView();
  return (
  <section ref={ref} className={`tz tz--white ${seen ? "tz--in" : ""}`} id="dashboard">
    <div className="tz__container">
      <Head badge={DASHBOARD.badge} title={DASHBOARD.title} />
      <p className="tz__lead tz__r" style={d(3)}>{DASHBOARD.text}</p>

      <div className="tz__dash">
        <div className="tz__frame tz__r tz__r--left">
          <div className="tz__frame-inner">
            <span className="tz__tag">TPMS DASHBOARD</span>
            <img src={IMAGES.dashboard} alt="TPMS monitoring dashboard" loading="lazy" className="tz__img" />
          </div>
        </div>

        <div className="tz__dash-card tz__r tz__r--right" style={d(2)}>
          <h3 className="tz__h3">
            <span className="tz__icon tz__icon--sm"><Icon name="dash" size={20} /></span>
            {DASHBOARD.listTitle}
          </h3>
          <ul className="tz__checks">
            {DASHBOARD.items.map((it, i) => (
              <li key={it} className="tz__li" style={d(i)}>
                <span className="tz__tick"><Icon name="check" size={13} /></span>
                {it}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="tz__bar tz__r" style={d(4)}>{DASHBOARD.note}</p>
    </div>
  </section>
  );
};

/* ============================================================
   3. DEPLOYMENT
   ============================================================ */
export const TPMSDeployment = () => {
  const [ref, seen] = useInView();
  return (
  <section ref={ref} className={`tz tz--dark ${seen ? "tz--in" : ""}`} id="deployment">
    <div className="tz__container">
      <Head badge={DEPLOY.badge} title={DEPLOY.title} light />
      <p className="tz__lead tz__lead--light tz__r" style={d(3)}>{DEPLOY.text}</p>
      <div className="tz__dep-grid">
        {DEPLOY.items.map((dep, i) => (
          <article className="tz__dep tz__r" style={{ "--i": i }} key={dep.title}>
            <span className="tz__dep-num">{String(i + 1).padStart(2, "0")}</span>
            <span className="tz__icon tz__icon--dark"><Icon name={dep.icon} /></span>
            <h3>{dep.title}</h3>
            <p>{dep.text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   4. WHY CHOOSE
   ============================================================ */
export const TPMSWhyChoose = () => {
  const [ref, seen] = useInView();
  return (
  <section ref={ref} className={`tz tz--sky ${seen ? "tz--in" : ""}`} id="why-tpms">
    <div className="tz__container tz__why">
      <div className="tz__why-text tz__r tz__r--left">
        <span className="tz__pill">{WHY.badge}</span>
        <h2 className="tz__title tz__title--left">{WHY.title}</h2>
        <p className="tz__subtitle tz__subtitle--left">{WHY.subtitle}</p>
        <span className="tz__underline tz__underline--left" />
        {WHY.paragraphs.map((p) => (
          <p className="tz__para" key={p}>{p}</p>
        ))}
      </div>

      <div className="tz__why-card tz__r tz__r--right" style={d(2)}>
        <h3 className="tz__h3">{WHY.listTitle}</h3>
        <ul className="tz__why-list">
          {WHY.items.map((it, i) => (
            <li key={it} className="tz__li" style={d(i)}>
              <span className="tz__why-n">{String(i + 1).padStart(2, "0")}</span>
              {it}
            </li>
          ))}
        </ul>
        <p className="tz__why-close">{WHY.closing}</p>
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   5. CTA
   ============================================================ */
export const TPMSCta = () => {
  const [ref, seen] = useInView();
  return (
  <section ref={ref} className={`tz tz--sky tz--cta ${seen ? "tz--in" : ""}`} id="tpms-cta">
    <div className="tz__container">
      <div className="tz__cta tz__r tz__r--zoom">
        <h2>{CTA.title}</h2>
        <p className="tz__cta-sub">{CTA.subtitle}</p>
        {CTA.paragraphs.map((p) => (
          <p className="tz__cta-text" key={p}>{p}</p>
        ))}
        <p className="tz__cta-strap">{CTA.strap}</p>
        <div className="tz__cta-btns">
          <Link to={LINKS.demo} className="tz__btn tz__btn--solid">{CTA.primary}</Link>
          <Link to={LINKS.team} className="tz__btn tz__btn--ghost">{CTA.secondary}</Link>
        </div>
        <p className="tz__cta-tag">{CTA.tagline}</p>
      </div>
    </div>
  </section>
  );
};

/* sab ek saath lagana ho to */
const TPMSOperations = () => (
  <>
    <TPMSConditions />
    <TPMSDashboard />
    <TPMSDeployment />
    <TPMSWhyChoose />
    <TPMSCta />
  </>
);

export default TPMSOperations;